import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowUpRight, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Container, Reveal } from "@/components/Reveal";
import ServiceIcon from "@/components/ServiceIcon";
import { SERVICES, COMPANY } from "@/data/content";

export default function SolutionDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/solutions" replace />;

  const related = SERVICES.filter((s) => s.slug !== slug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    provider: { "@type": "Organization", name: COMPANY.name },
    areaServed: "United States",
    description: service.short,
  };

  return (
    <>
      <SEO
        title={service.title}
        description={service.short}
        path={`/solutions/${slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        overline="Solutions"
        titleLines={[service.title]}
        subtitle={service.intro}
        crumbs={[
          { label: "Solutions", path: "/solutions" },
          { label: service.title, path: `/solutions/${slug}` },
        ]}
        image={service.image}
      >
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-electric px-8 py-4 text-sm font-medium text-white transition-transform hover:scale-[0.97] hover:bg-electric-hover"
        >
          Discuss {service.title} <ArrowUpRight className="h-4 w-4" />
        </Link>
      </PageHero>

      {/* Overview */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="flex h-14 w-14 items-center justify-center border border-line text-electric">
                <ServiceIcon name={service.icon} className="h-7 w-7" />
              </div>
              <h2 className="mt-6 font-heading text-2xl font-semibold tracking-tight text-navy md:text-3xl">
                Overview
              </h2>
            </div>
            <div className="lg:col-span-8">
              <p className="text-xl leading-relaxed text-navy/80">
                {service.overview}
              </p>
            </div>
          </div>
        </Container>
      </section>

      {service.availabilityNote && (
        <section className="bg-navy py-8">
          <Container>
            <div className="flex items-center gap-4 text-white">
              <MapPin className="h-5 w-5 shrink-0 text-energy" />
              <p className="font-mono text-sm uppercase tracking-[0.14em]">
                {service.availabilityNote}
              </p>
            </div>
          </Container>
        </section>
      )}

      {/* Benefits */}
      <section className="bg-ice py-24 md:py-28">
        <Container>
          <p className="overline text-electric">Why it matters</p>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-semibold tracking-tight text-navy md:text-4xl">
            The value we bring to {service.title.toLowerCase()}.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
            {service.benefits.map((b, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08}>
                <div className="flex items-start gap-4 border border-line bg-white p-7">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-energy/12 text-energy">
                    <Check className="h-4 w-4" />
                  </span>
                  <p className="text-navy/80">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Features */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <div className="grid grid-cols-1 border-l border-t border-line sm:grid-cols-2">
            {service.features.map((f, i) => (
              <Reveal key={i} delay={(i % 2) * 0.08} className="border-b border-r border-line">
                <div className="p-8 md:p-10">
                  <span className="font-mono text-sm text-slatey">0{i + 1}</span>
                  <h3 className="mt-4 font-heading text-xl font-medium text-navy">
                    {f.title}
                  </h3>
                  <p className="mt-3 leading-relaxed text-slatey">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Process */}
      <section className="bg-navy-dark py-24 text-white md:py-28">
        <Container>
          <p className="overline text-energy">How we work</p>
          <h2 className="mt-4 font-heading text-3xl font-semibold tracking-tight md:text-4xl">
            A disciplined, four-step process.
          </h2>
          <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {service.process.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="h-full bg-navy-dark p-8">
                  <span className="font-mono text-3xl text-energy">0{i + 1}</span>
                  <h3 className="mt-5 font-heading text-xl font-medium">{p.step}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/65">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* Related */}
      <section className="bg-white py-24 md:py-28">
        <Container>
          <h2 className="font-heading text-2xl font-semibold tracking-tight text-navy md:text-3xl">
            Explore other solutions
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/solutions/${r.slug}`}
                className="group flex items-center justify-between border border-line p-8 transition-colors hover:border-electric"
              >
                <div className="flex items-center gap-5">
                  <div className="flex h-12 w-12 items-center justify-center border border-line text-electric">
                    <ServiceIcon name={r.icon} className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-medium text-navy">
                      {r.title}
                    </h3>
                    <p className="mt-1 text-sm text-slatey">{r.short}</p>
                  </div>
                </div>
                <ArrowUpRight className="ml-4 h-5 w-5 shrink-0 text-slatey transition-colors group-hover:text-electric" />
              </Link>
            ))}
          </div>
        </Container>
      </section>

      <CTABand />
    </>
  );
}
